import { useState } from "react";
import * as Yup from "yup";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { NextButton, ResetButton } from "../../twStyles";
import { useAuth } from "../../../../hooks/auth";
import { useToast } from "../../../../hooks/toast";
import refreshToken from "../../../../utils/refreshToken";
import api from "../../../../services/api";
import getValidationErrors from "../../../../utils/getValidationError";
import SaveDraftButton from "../../../../components/SaveDraftButton";
import {
  FormStack,
  PropertyStepActions,
} from "../../../../components/propertyDescription/descriptionUi";
import AccommodationsSection from "../DescriptionForm/sections/AccommodationsSection";
import AmenitiesSection from "../DescriptionForm/sections/AmenitiesSection";
import CondominiumSection from "../DescriptionForm/sections/CondominiumSection";
import BenefitsSection from "./BenefitsSection";
import {
  FURNITURE_STATUS_INVALID_MESSAGE,
  FURNITURE_STATUS_REQUIRED_MESSAGE,
  FURNITURE_STATUS_VALUES,
} from "../../../../utils/propertyFurniture";

type BenefitFlags = Record<string, boolean>;

const INITIAL_BENEFITS: BenefitFlags = {
  is_nearby_clinics: false,
  is_nearby_major_roads: false,
  is_nearby_schools: false,
  is_nearby_mall: false,
  is_nearby_public_transport: false,
  has_24h_concierge: false,
  has_ac: false,
  has_backyard: false,
  has_bbq: false,
  has_cable_tv: false,
  has_elevator: false,
  has_game_room: false,
  has_garden: false,
  has_gourmet: false,
  has_gourmet_porch: false,
  has_gym: false,
  has_home_cinema: false,
  has_party: false,
  has_playground: false,
  has_pool: false,
  has_porch: false,
  has_sauna: false,
  has_sea_view: false,
  has_spa: false,
  has_sports: false,
  has_storage: false,
  has_tennis_court: false,
};

function mapInitialBenefits(raw?: Record<string, unknown>): BenefitFlags {
  if (!raw) return INITIAL_BENEFITS;
  return Object.keys(INITIAL_BENEFITS).reduce((acc, key) => {
    acc[key] = Boolean(Number(raw[key] ?? 0));
    return acc;
  }, { ...INITIAL_BENEFITS });
}

type Props = {
  propertyID: number | undefined;
  initialBenefits?: Record<string, unknown>;
  rooms?: number;
  suites?: number;
  bathrooms?: number;
  garages?: number;
  condominium: string;
  permissionFlags: Record<string, number>;
  isSavingPropertyDraft: boolean;
  onChangeProperty: (field: string, value: unknown) => void;
  onSavePropertyDraft: () => Promise<void>;
  onBack: () => void;
  onNext: () => void;
};

const characteristicsFieldsSchema = Yup.object().shape({
  rooms: Yup.number()
    .required("Número de quartos obrigatório")
    .min(1, "Deve conter no mínimo 1 quarto"),
  suites: Yup.number()
    .required("Número de suítes obrigatório")
    .max(5, "Número máximo de suítes atingido"),
  bathrooms: Yup.number()
    .required("Número de banheiros obrigatório")
    .max(10, "Número máximo de banheiros atingido"),
  garages: Yup.number()
    .required("Número de vagas de garagem obrigatório")
    .max(5, "Número máximo de vagas atingido"),
  is_furnished: Yup.number()
    .oneOf([...FURNITURE_STATUS_VALUES], FURNITURE_STATUS_INVALID_MESSAGE)
    .required(FURNITURE_STATUS_REQUIRED_MESSAGE),
  allow_pets: Yup.number().required("Informe se o imóvel permite pets"),
});

export default function CharacteristicsForm({
  propertyID,
  initialBenefits,
  rooms,
  suites,
  bathrooms,
  garages,
  condominium,
  permissionFlags,
  isSavingPropertyDraft,
  onChangeProperty,
  onSavePropertyDraft,
  onBack,
  onNext,
}: Props) {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();
  const [benefits, setBenefits] = useState(() =>
    mapInitialBenefits(initialBenefits)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  function handleBenefitChange(identifier: string, value: boolean) {
    setBenefits((prev) => ({ ...prev, [identifier]: value }));
  }

  function reportApiErrors(err: any) {
    const apiErrors = err?.response?.data?.errors;
    if (
      apiErrors != null &&
      typeof apiErrors === "object" &&
      !Array.isArray(apiErrors)
    ) {
      Object.values(apiErrors).forEach((messages) => {
        if (Array.isArray(messages)) {
          messages.forEach((msg: string) => {
            addToast({ title: msg, type: "error" });
          });
        }
      });
    }

    if (err?.response?.data?.message) {
      addToast({
        title: err.response.data.message,
        type: "error",
      });
    }
  }

  async function persistBenefits() {
    if (!propertyID) {
      addToast({
        title: "Salve a publicação do imóvel antes de continuar.",
        type: "error",
      });
      return false;
    }

    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
    const response = await api.put(
      `/api/adm/properties/${propertyID}/benefits`,
      benefits
    );
    return Boolean(response.data.success);
  }

  async function validateMovedFields() {
    await characteristicsFieldsSchema.validate(
      {
        rooms,
        suites,
        bathrooms,
        garages,
        is_furnished: permissionFlags.is_furnished,
        allow_pets: permissionFlags.allow_pets,
      },
      { abortEarly: false }
    );
  }

  async function handleSaveDraft() {
    if (isSavingDraft || isSubmitting) return;
    setIsSavingDraft(true);
    try {
      await onSavePropertyDraft();
      const ok = await persistBenefits();
      if (ok) {
        addToast({
          title: "Rascunho salvo com sucesso!",
          type: "success",
        });
      }
    } catch (err: any) {
      reportApiErrors(err);
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handleSubmit() {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await validateMovedFields();
      await onSavePropertyDraft();
      const ok = await persistBenefits();
      if (ok) {
        onNext();
        addToast({
          title: "Características cadastradas!",
          type: "success",
        });
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        const firstKey = Object.keys(error)[0];
        if (firstKey) {
          addToast({ title: error[firstKey], type: "info", description: "" });
        }
      } else {
        reportApiErrors(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full min-w-0">
      <FormStack>
        <AccommodationsSection
          rooms={rooms}
          suites={suites}
          bathrooms={bathrooms}
          garages={garages}
          onChange={onChangeProperty}
        />
        <AmenitiesSection
          permissionFlags={permissionFlags}
          onChange={onChangeProperty}
        />
        <CondominiumSection
          condominium={condominium}
          onChange={(value) => onChangeProperty("condominium", value)}
        />
        <BenefitsSection values={benefits} onChange={handleBenefitChange} />
      </FormStack>

      <PropertyStepActions
        leading={
          <>
            <ResetButton
              type="button"
              onClick={() => setBenefits(INITIAL_BENEFITS)}
            >
              LIMPAR
            </ResetButton>
            <SaveDraftButton
              isLoading={isSavingDraft || isSavingPropertyDraft}
              disabled={!propertyID || isSubmitting}
              onClick={handleSaveDraft}
            >
              Salvar como rascunho
            </SaveDraftButton>
          </>
        }
        trailing={
          <>
            <NextButton
              type="button"
              className="inline-flex h-10 gap-1.5 py-0 [&>svg]:!ml-0"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4 text-white" aria-hidden />
              <p>Voltar</p>
            </NextButton>
            <NextButton
              type="button"
              className="inline-flex h-10 gap-1.5 py-0"
              onClick={isSubmitting ? () => {} : handleSubmit}
            >
              <p>{isSubmitting ? "Carregando..." : "Próximo"}</p>
              {!isSubmitting && (
                <ArrowRight className="h-4 w-4 text-white" aria-hidden />
              )}
            </NextButton>
          </>
        }
      />
    </div>
  );
}
